import styled from '@emotion/styled';
import React, { useState, useEffect } from 'react';
import { ProgressBar } from 'react-bootstrap';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { AiOutlineClose, AiOutlineReload } from 'react-icons/ai';
import { BsCheck2Circle } from 'react-icons/bs';

import BayTooltip from '../../BayTooltip';
import IconButton from '../../IconButton';
import { getBytes } from '../../utilities/constants/filesConstants';
import { getIconByFileType } from '../../utilities/fileExtensionHandler';

type StatusType = 'success' | 'error' | 'cancel' | 'progress' | 'invalid';

interface UploadFile {
	filename: string;
	type: string;
	size?: number;
}

interface ResponseType {
	type: StatusType;
	text: string;
	textColor: string;
}

interface UploadProgressBarProps {
	file: UploadFile;
	fileIndex: number;
	cancelFileUpload: (index: number) => void;
	retryFileUpload: (index: number) => void;
	enableCancel?: boolean;
	progressValue?: number;
	statusType?: StatusType;
	errorMessage?: string;
	canRetry?: boolean;
	enforceVideoConstraints?: boolean;
}

const CloseIcon = styled.div`
	.icon-hover:hover {
		color: var(--bs-blue) !important;
		background-color: var(--bs-blue-100) !important;
		border-radius: 2px;
		padding-bottom: 3px;
	}
	.uploading-file-success-icon {
		color: var(--bs-green-400);
	}
	.disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;

const ErrorMessage = styled.div`
	color: var(--bs-red);
	margin-top: 4px;
	font-size: 0.875rem;
`;

const responseTypes: Record<StatusType, ResponseType> = {
	success: {
		type: 'success',
		text: 'File successfully uploaded.',
		textColor: 'var(--bs-green-400)',
	},
	error: {
		type: 'error',
		text: 'There was an error and your file could not be uploaded. Please check the file type and size and try again.',
		textColor: 'var(--bs-red)',
	},
	cancel: {
		type: 'cancel',
		text: 'File upload was cancelled. Would you like to try again?',
		textColor: 'var(--bs-red)',
	},
	progress: {
		type: 'progress',
		text: '',
		textColor: '',
	},
	invalid: {
		type: 'invalid',
		text: 'Invalid file. Please check the requirements and try again.',
		textColor: 'var(--bs-red)',
	},
};

const UploadProgressBar: React.FC<UploadProgressBarProps> = ({
	file,
	fileIndex,
	cancelFileUpload,
	retryFileUpload,
	enableCancel = false,
	progressValue = 0,
	statusType = 'progress',
	errorMessage = '',
	canRetry = true,
	enforceVideoConstraints = false,
	showFileDetails = true,
	showProgressLabel = true,
}) => {
	const [label, setLabel] = useState('');

	// Determine which status object to use based on the statusType prop
	const getResponseType = () => {
		if ((statusType === 'invalid' || statusType === 'error') && errorMessage) {
			return {
				type: statusType,
				text: errorMessage,
				textColor: 'var(--bs-red)',
			};
		}
		return responseTypes[statusType] || responseTypes.progress;
	};

	const responseType = getResponseType();

	// Set "Processing" label when progress reaches 100%
	useEffect(() => {
		if (progressValue === 100 && statusType === 'progress') {
			const timer = setTimeout(() => {
				setLabel('Processing');
			}, 1000);
			return () => clearTimeout(timer);
		} else {
			setLabel('');
		}
	}, [progressValue, statusType]);

	const onCancel = () => {
		cancelFileUpload(fileIndex);
	};

	const onRetry = () => {
		if (canRetry) {
			retryFileUpload(fileIndex);
		}
	};

	const getProgressIcon = (type: StatusType): React.ReactNode => {
		// For file size errors, always hide the retry button if canRetry is false
		if (!canRetry) {
			return null;
		}

		// Check for all non-retryable error conditions - never show retry for these
		if (errorMessage) {
			// Build list of non-retryable errors based on context
			const nonRetryableErrors = [
				'file must not exceed',
				'Video file must not exceed 500MB',
				'Video file has an unsupported codec',
				...(enforceVideoConstraints ? [
					'Video file must be between 3 seconds and 5 minutes',
					'Video file must be between 1:1 and 16:9 aspect ratio',
				] : []),
				'File name must be a minimum of 3 characters',
				'File name contains characters that are not allowed',
				'File name contains extra spaces that are not allowed',
				'Unsupported video format or codec',
				'DEMUXER_ERROR_NO_SUPPORTED_STREAMS',
			];

			// Check if error message indicates this file should not be retried
			const shouldPreventRetry = nonRetryableErrors.some((errorText) =>
				errorMessage.includes(errorText),
			);

			// Additional checks for file type errors which often have custom messages
			const isFileTypeError =
				errorMessage.includes('file type') ||
				errorMessage.includes('file format') ||
				errorMessage.includes('unsupported') ||
				errorMessage.includes('codec');

			if (shouldPreventRetry || isFileTypeError) {
				return null;
			}
		}

		switch (type) {
		case 'success':
			return (
				<CloseIcon>
					<BsCheck2Circle className="uploading-file-success-icon" />
				</CloseIcon>
			);
		case 'error':
		case 'cancel':
		case 'invalid':
			return (
				<div className="retry-btn">
					<IconButton
						className="pt-0"
						placement="top"
						tooltipContent="Retry"
						variant="none"
						onClickFunc={() => onRetry()}
					>
						<CloseIcon className="uploading-file-close-icon">
							<span className="icon-hover p-1">
								<AiOutlineReload />
							</span>
						</CloseIcon>
					</IconButton>
				</div>
			);
		case 'progress':
			if (!enableCancel) {
				return null;
			}
			return (
				<div className="retry-btn">
					<IconButton
						className="pt-0"
						placement="top"
						tooltipContent="Cancel"
						variant="none"
						onClickFunc={() => onCancel()}
					>
						<CloseIcon className="uploading-file-close-icon">
							<span className="icon-hover p-1">
								<AiOutlineClose />
							</span>
						</CloseIcon>
					</IconButton>
				</div>
			);
		default:
			return null;
		}
	};

	return (
		<Container className="p-0">
			{showFileDetails && (
				<Row className="mb-2 px-3 align-content-center align-items-center flex-nowrap">
					<Col className="px-1 filetype-icon" xs={1}>
						{getIconByFileType(file.type)}
					</Col>
					<Col
						className="attachment-model-fileName flex-1 px-1 align-content-center align-items-center"
						style={{
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
							overflow: 'hidden',
						}}
						xs={6}
					>
						<BayTooltip overlay={file.filename} placement="top">
							<span className="text-truncate">{file.filename}</span>
						</BayTooltip>
					</Col>
					{file?.size && (
						<Col
							className="px-1 text-end justify-content-end"
							style={{
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
								overflow: 'hidden',
							}}
							xs={3}
						>{`${`(${getBytes(file.size)})`}`}</Col>
					)}
					<Col
						className="px-1 justify-content-center"
						style={{ textAlign: 'right' }}
						xs={1}
					>
						{getProgressIcon(responseType.type)}
					</Col>
				</Row>
			)}
			{responseType.type === 'progress' && (
				<ProgressBar
					label={showProgressLabel ? (label ? label : `${progressValue}%`) : ''}
					now={progressValue}
				/>
			)}
			{responseType.type !== 'progress' && (
				<ErrorMessage style={{ color: responseType.textColor }}>
					{responseType.text}
				</ErrorMessage>
			)}
		</Container>
	);
};

export default UploadProgressBar;
